import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import assets from "../../assets";
import useHandler from "./handler";
import { COMMUNICATIVE_NAV_ITEMS } from "../../utils/constant";
import { useAuth } from "../../hooks/use-auth";
import {LogoutPopup} from "../../components/logout-popup";
import { ROUTE_PROFILE } from "../../route/communicative";
import "./style.sass";

const CIRCLE_SIZE = 110;
const STROKE_WIDTH = 9;
const RADIUS = (CIRCLE_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const {
    loading,
    data,
    skillList,
    strengths,
    areasToImprove,
    activityWithMeta,
    nextStepsWithMeta
  } = useHandler();

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  const renderNavItem = ({ path, label }) => {
    const isActive = path && location.pathname === path;
    return (
      <span
        key={path || label}
        className={`nav_link ${isActive ? "active" : ""}`}
        onClick={() => path && navigate(path)}
      >
        {label}
        {label === "Exams" && <span className="new_badge">New</span>}
      </span>
    );
  };

  const TopNav = ({ userName, userEmail }) => (
    <header className="dashboard_nav">
      <div className="nav_inner">
        <div className="nav_left">
          <img src={assets.ic_logo} alt="Rovian AI" className="nav_logo" />
          <span className="logo_title">Rovian AI</span>
          <nav className="nav_links">{COMMUNICATIVE_NAV_ITEMS.map(renderNavItem)}</nav>
        </div>
        <div className="nav_user_container">
          <div className="nav_user" onClick={() => setShowDropdown(!showDropdown)}>
            <div className="nav_avatar">{userName?.[0] || "U"}</div>
            <div className="nav_user_info">
              <span className="nav_user_name">{userName}</span>
              <span className="nav_user_email">{userEmail}</span>
            </div>
            <span className={`nav_caret ${showDropdown ? "open" : ""}`}>
              <img src={assets.ic_chevron_down} width={14} height={14} alt="arrow" />
            </span>
          </div>

          {showDropdown && (
            <div className="profile_dropdown">
              <div className="dropdown_item" onClick={() => navigate(ROUTE_PROFILE)}>
                <img src={assets.ic_profile_chat} alt="profile" />
                <span>Profile</span>
              </div>
              <div
                className="dropdown_item logout"
                onClick={() => {
                  setShowLogoutModal(true);
                  setShowDropdown(false);
                }}
              >
                <img src={assets.ic_logout} alt="logout" />
                <span>Log out</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );

  const SparkleIcon = ({ size = 20, className = "" }) => (
  <svg
    className={`sparkle_icon ${className}`}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M12 2C12.5 7.5 14 10.5 17 12C14 13.5 12.5 16.5 12 22C11.5 16.5 10 13.5 7 12C10 10.5 11.5 7.5 12 2Z"
      fill="currentColor"
    />
  </svg>
);

 const OverallScoreCard = ({ score, statusLabel, statusNote, max = 100 }) => {
    const percent = Math.max(0, Math.min(100, (score / max) * 100));
    const dashOffset = CIRCUMFERENCE - (percent / 100) * CIRCUMFERENCE;
 
    return (
      <div className="progress_card overall_score_card">
        <SparkleIcon className="sparkle_top_right" size={28} />
        <SparkleIcon className="sparkle_bottom_right" size={16} />
 
        <div className="stat_card_header">
          <span className="overall_title">Overall Average Score</span>
        </div>
        <div className="overall_score_body">
          <div className="overall_circle_wrap">
            <svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}>
              <circle
                cx={CIRCLE_SIZE / 2}
                cy={CIRCLE_SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="rgba(255,255,255,0.25)"
                strokeWidth={STROKE_WIDTH}
              />
              <circle
                cx={CIRCLE_SIZE / 2}
                cy={CIRCLE_SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="#FFFFFF"
                strokeWidth={STROKE_WIDTH}
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
                transform={`rotate(-90 ${CIRCLE_SIZE / 2} ${CIRCLE_SIZE / 2})`}
              />
            </svg>
            <div className="overall_circle_text">
              <span className="overall_circle_value">{score}</span>
              <span className="overall_circle_caption">Overall</span>
            </div>
          </div>
          <div className="overall_score_info">
            <strong>{statusLabel}</strong>
            <p>{statusNote}</p>
          </div>
        </div>
      </div>
    );
  };

  const TotalAttemptsCard = ({ total }) => (
    <div className="progress_card">
      <div className="stat_card_header">
        <span>Total Attempts</span>
        <span className="stat_icon"><img src={assets.ic_attempts} alt="skill"/></span>
      </div>
      <div className="stat_big_number">{total}</div>
      <p className="stat_caption">Total Activities attempted across all skills</p>
    </div>
  );

  const LearningStatusCard = ({ label, note, tag }) => (
    <div className="progress_card">
      <div className="stat_card_header">
        <span>Learning Status</span>
        <span className="stat_icon"><img src={assets.ic_learning_status} alt="skill"/></span>
      </div>
      <strong className="stat_status_label">{label}</strong>
      <p className="stat_caption">{note}</p>
      <span className="status_tag">{tag}</span>
    </div>
  );

  const SkillCard = ({ icon, label, score, attempts, status, color }) => (
    <div className="skill_card">
      <div className="skill_card_top">
        <span className={`skill_icon skill_icon_${color}`}><img src={icon} alt="skill"/></span>
        <span className="skill_label">{label}</span>
        <span className="skill_score">{score}%</span>
      </div>
      <div className="skill_bar_track">
        <div className={`skill_bar_fill skill_bar_${color}`} style={{ width: `${score}%` }} />
      </div>
      <div className="skill_card_bottom">
        <span>{attempts} Attempts</span>
        <span className={`skill_status skill_status_${color}`}>{status}</span>
      </div>
    </div>
  );

  const SidePanelList = ({ title, icon, items, tone }) => (
    <div className={`side_panel side_panel_${tone}`}>
      <div className="side_panel_title">
        <img src={icon} alt="strength"/>
        {title}
      </div>
      <p className="side_panel_subtitle">
        {tone === "good" ? "Great job! You're strong in these areas." : "Focus on these skills to level up."}
      </p>
      <div className="side_panel_items">
        {items.map((item) => (
          <div key={item.key} className="side_panel_item">
            <span className={`side_panel_icon side_panel_icon_${item.color}`}><img src={item.icon} alt="skill"/></span>
            <span className="side_panel_label">{item.label}</span>
            <span className="side_panel_score"><span className="score_total">{item.score}</span>/100</span>
          </div>
        ))}
      </div>
    </div>
  );

 const RecentActivity = ({ items }) => (
  <div className="activity_card">
    <div className="activity_title">
      <img src={assets.ic_timer_grey} alt="time" /> Recent Activity
    </div>
    {items.length === 0 ? (
      <div className="empty_state">No recent activity yet. Start practicing to see your progress here!</div>
    ) : (
      <div className="activity_table">
        <div className="activity_row activity_header_row">
          <span>Activity</span>
          <span>Date &amp; Time</span>
        </div>
        {items.map((item, i) => (
          <div key={i} className="activity_row">
            <div className="activity_left">
              <span className={`activity_dot activity_dot_${item.color}`} />
              <span className={`activity_chip activity_chip_${item.color}`}>
                <img src={item.icon} alt="skill" />
              </span>
              <span className="activity_label">{item.label}</span>
            </div>
            <span className="activity_date">{item.date}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

  const RecommendedNextSteps = ({ items }) => (
    <div className="next_steps_panel">
      <div className="next_steps_title">
            <img src={assets.ic_recommend} alt="time" className="next_steps_icon"/>
        Recommended Next Steps
      </div>
      <div className="next_steps_list">
        {items.map((item, i) => (
          <div key={i} className="next_step_item">
            <span className={`next_step_chip next_step_chip_${item.color}`}><img src={item.icon} alt="skill"/></span>
            <p>{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading || !data) {
    return (
      <div className="dashboard_page">
        <div className="dashboard_loading">Loading dashboard…</div>
      </div>
    );
  }

  return (
    <div className="dashboard_page">
      <TopNav userName={data.user.name} userEmail={data.user.email} />

      <main className="dashboard_main">
        <h1>Good morning, {data.user.name}.</h1>
        <p className="dashboard_subtitle">
          Your complete learning overview with scores, insights, and personalized recommendations.
        </p>

        <div className="top_stats_row">
          <OverallScoreCard
            score={data.overallScore}
            statusLabel={data.overallStatusLabel}
            statusNote={data.overallStatusNote}
          />
          <TotalAttemptsCard total={data.totalAttempts} />
          <LearningStatusCard {...data.learningStatus} />
        </div>

        <div className="dashboard_grid">
          <div className="dashboard_grid_main">
            <div className="panel_card">
              <div className="panel_title">Skill Performance Overview</div>
              <div className="skills_grid">
                {skillList.map((skill) => (
                  <SkillCard key={skill.key} {...skill} />
                ))}
              </div>
            </div>

            <div className="panel_card">
              <RecentActivity items={activityWithMeta} />
            </div>
          </div>

          <div className="dashboard_grid_side">
            <SidePanelList title="Strengths" icon={assets.ic_strength} items={strengths} tone="good" />
            <SidePanelList title="Areas to Improve" icon={assets.ic_improve} items={areasToImprove} tone="warn" />
            <RecommendedNextSteps items={nextStepsWithMeta} />
          </div>
        </div>
      </main>

      {showLogoutModal && (
        <LogoutPopup
          title="Logout"
          description="You'll be returned to the login screen. Any unsaved changes will be lost."
          btnText="Logout"
          onClose={() => setShowLogoutModal(false)}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
};

export default Dashboard;